import React, { useContext, useEffect, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  type HeroImageV1,
  type RestLCOModal,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  CloseOfferButton,
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestLCOModal;
  detailedAction: React.ReactElement;
  close: () => void;
};

const CopyContent = styled.div`
  overflow-y: auto;
  padding-top: 10px;
  margin-bottom: 20px;
  max-height: 400px;
`;

export const LCOModal: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      hero,
      info: { title: titleP, details },
    },
  },
  detailedAction,
  close,
}) => {
  const { mobileView } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;

  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );

  const imgUrl = (hero as HeroImageV1).url;

  const title = <TitleTheme title={titleP} />;
  const copy = <CustomerHtml value={details} />;

  if (mobileView) {
    return (
      <ModalMobileInnerSpacer
        className={cs('display-grid', 'sixty-forty', 'mobile', 'inner-spacer')}
        style={{
          borderRadius: theme.borderRadius,
          overflow: 'hidden',
        }}
      >
        <OfferTitleMobile title={title} close={close} />
        <img
          alt=""
          src={imgUrl}
          css={css`
            width: 100%;
            height: 100%;
            max-height: 300px;
            object-fit: cover;
            object-position: center;
            border-radius: 4px;
          `}
        />
        <CopyContent>{copy}</CopyContent>
        <div>{detailedAction}</div>
      </ModalMobileInnerSpacer>
    );
  }

  return (
    <ModalDesktopInnerSpacer
      css={css`
        padding: 0;
      `}
      className={cs('lco-modal', 'display-grid', 'desktop', 'inner-spacer')}
      style={{
        borderRadius: theme.borderRadius,
        overflow: 'hidden',
      }}
    >
      <img
        ref={imageRef}
        alt=""
        src={imgUrl}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        `}
      />
      <div
        css={css`
          padding: 1rem;
        `}
        className={cs('display-grid', 'content-grid')}
      >
        <CloseOfferButton
          newVariant
          onClick={close}
          className={cs('justify-self--end', 'z-10')}
        />
        <CopyContent className={cs('align-self--center')}>
          <span ref={titleRef}>
            <TitleTheme title={titleP} css={{ marginBottom: '1rem' }} />
          </span>
          <div ref={descRef}>{copy}</div>
        </CopyContent>
        <div ref={actionsRef}>{detailedAction}</div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};
